import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { FaPowerOff, FaSyncAlt } from "react-icons/fa";
import http from "../../api/apiClient";
import { refreshEBoekhoudenStatus } from "../../hooks/useIntegrationStatus";
import { eBoekhoudenDotClass, eBoekhoudenStateLabel, useEBoekhoudenStatus } from "../../components/eboekhouden/EBoekhoudenStatusDot";
import { formatDutchDate } from "../../utils/dutchDate";
import { parseToDate } from "../../utils/dateUtils";

/**
 * The connection itself: its status (checked every 5 minutes), check again, and switch it on or off. Switched off,
 * nothing is fetched from e-Boekhouden; the settings below are kept.
 */
export default function EBoekhoudenConnection({ canManage }: { canManage: boolean }): ReactElement {
  const { t } = useTranslation();
  const { checking, state, data } = useEBoekhoudenStatus(true);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const enabled = data?.enabled !== false;

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setFailed(false);
    try {
      await action();
      await refreshEBoekhoudenStatus();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const checkedAt = parseToDate(data?.checkedAt);
  const checkedTime = checkedAt
    ? `${formatDutchDate(data?.checkedAt)} ${String(checkedAt.getHours()).padStart(2, "0")}:${String(checkedAt.getMinutes()).padStart(2, "0")}`
    : "";

  return (
    <section
      aria-labelledby="eboekhouden-connection-title"
      className="rounded-[24px] border border-slate-200/80 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80 sm:p-6"
    >
      <div className="flex flex-wrap items-center gap-4">
        <span className={`h-3.5 w-3.5 shrink-0 rounded-full ${eBoekhoudenDotClass(state, checking)}`} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <h2 id="eboekhouden-connection-title" className="text-base font-black text-slate-950 dark:text-white">
            {eBoekhoudenStateLabel(t, state, checking)}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            {!enabled
              ? t("finance.eboekhouden.off_help", { defaultValue: "Nothing is fetched from e-Boekhouden. The settings below are kept." })
              : checkedTime
                ? t("finance.eboekhouden.checked_at", { time: checkedTime, defaultValue: "Last checked {{time}} · checked every 5 minutes" })
                : t("finance.eboekhouden.checked_every", { defaultValue: "Checked every 5 minutes" })}
            {data?.detail ? ` · ${data.detail}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {enabled && (
            <button
              type="button"
              disabled={busy}
              onClick={() => run(() => http.get("/finance/settings/eboekhouden/status", { params: { refresh: true } }))}
              className="inline-flex h-9 items-center gap-2 rounded-xl border border-slate-200 px-3 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-white/10 dark:text-slate-200 dark:hover:bg-white/5"
            >
              <FaSyncAlt aria-hidden="true" className={busy ? "animate-spin" : ""} />
              {t("finance.eboekhouden.check_now", { defaultValue: "Check now" })}
            </button>
          )}
          {canManage && (
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              disabled={busy}
              onClick={() => run(() => http.put("/finance/settings/eboekhouden/enabled", { enabled: !enabled }))}
              className={`inline-flex h-9 items-center gap-2 rounded-xl px-3 text-xs font-bold disabled:opacity-60 ${enabled
                ? "border border-rose-200 text-rose-700 hover:bg-rose-50 dark:border-rose-400/30 dark:text-rose-200 dark:hover:bg-rose-400/10"
                : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
            >
              <FaPowerOff aria-hidden="true" />
              {enabled
                ? t("finance.eboekhouden.switch_off", { defaultValue: "Switch connection off" })
                : t("finance.eboekhouden.switch_on", { defaultValue: "Switch connection on" })}
            </button>
          )}
        </div>
      </div>
      {failed && (
        <p role="alert" className="mt-3 text-sm font-semibold text-rose-600">
          {t("finance.eboekhouden.action_failed", { defaultValue: "That did not work. Please try again." })}
        </p>
      )}
    </section>
  );
}
